import { mergeAttributes, Node } from "@tiptap/core";

import { setNodeViewAttrs } from "@/editor/adapters/utils";
import { findNodeWithPosition } from "@/editor/utils/nodes";
import { setRAFTimeout } from "@/utils/timeout";

import { NWTab, NWTabs, TabsProps } from ".";

type TabProps = {
  name: string;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    nwTabs: {
      /**
       * Creates a new tabs component with the specified options
       */
      setNWTabs: (options: TabsProps) => ReturnType;
      /**
       * Sets the active tab for a specific tabs component
       */
      setActiveTab: (tabsId: string, tabId: string) => ReturnType;
    };
    nwTab: {
      /**
       * Creates a new tab with the specified options
       */
      setNWTab: (options: TabProps) => ReturnType;
    };
  }
}

/**
 * Direction for tab animations
 */
type AnimationDirection = "left" | "right";

/**
 * Adapter for Tabs component integration with TipTap
 */
export const NWTabsAdapter = Node.create({
  name: "nw-tabs",
  group: "block",
  content: "tabs+",
  draggable: false,
  selectable: false,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute("id"),
      },
      active: {
        default: "",
        parseHTML: element => element.getAttribute("active"),
      },
      embedded: {
        default: false,
        parseHTML: element => element.getAttribute("embedded"),
      },
      largetabs: {
        default: false,
        parseHTML: element => element.getAttribute("largetabs"),
      },
      headingsize: {
        default: "md",
        parseHTML: element => element.getAttribute("headingsize"),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "nw-tabs",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["nw-tabs", mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement("nw-tabs") as NWTabs;
      setNodeViewAttrs(dom, node.attrs);

      let previousActiveTabId: string | null = node.attrs.active;
      let activeAnimationTimeoutId: number | null = null;

      /**
       * Handles animated transitions between tabs
       */
      const animateTabTransition = (prevTab: NWTab, currentTab: NWTab, direction: AnimationDirection): void => {
        if (direction === "right") {
          prevTab.animateOutLeft();
        } else {
          prevTab.animateOutRight();
        }

        activeAnimationTimeoutId = setRAFTimeout(() => {
          prevTab.hide();
          currentTab.show();

          if (direction === "right") {
            currentTab.animateInRight();
          } else {
            currentTab.animateInLeft();
          }

          previousActiveTabId = currentTab.getAttribute("id");
          activeAnimationTimeoutId = null;
        }, 150);
      };

      /**
       * Updates the visibility of tab content based on the active tab ID
       */
      const updateChildVisibility = (activeTabId: string | null | undefined): void => {
        if (activeAnimationTimeoutId !== null) {
          cancelAnimationFrame(activeAnimationTimeoutId);
          activeAnimationTimeoutId = null;
        }

        const childTabs = dom.querySelectorAll("nw-tab");
        const tabElements = Array.from(childTabs);

        if (previousActiveTabId && previousActiveTabId !== activeTabId) {
          const prevTabElement = tabElements.find(t => t.getAttribute("id") === previousActiveTabId) as
            | NWTab
            | undefined;

          const currentTabElement = tabElements.find(t => t.getAttribute("id") === activeTabId) as NWTab | undefined;

          if (prevTabElement && currentTabElement) {
            const prevIndex = tabElements.indexOf(prevTabElement);
            const currentIndex = tabElements.indexOf(currentTabElement);

            if (prevIndex !== -1 && currentIndex !== -1) {
              const animateFromRight = currentIndex > prevIndex;
              const direction: AnimationDirection = animateFromRight ? "right" : "left";

              animateTabTransition(prevTabElement, currentTabElement, direction);
              return;
            }
          }
        }

        childTabs.forEach((tab: Element) => {
          if (tab instanceof NWTab) {
            const tabIdAttr = tab.getAttribute("id");
            if (tabIdAttr === activeTabId) {
              tab.show();
              previousActiveTabId = tabIdAttr;
            } else {
              tab.hide();
            }
          }
        });
      };

      /**
       * Handles tab change events from the component
       */
      const onTabChange = ((event: CustomEvent) => {
        const tabId = event.detail.tabId;
        const currentPos = typeof getPos === "function" ? getPos() : undefined;

        if (currentPos === undefined || !editor || editor.isDestroyed) {
          console.warn("[NWTabsAdapter NodeView] Cannot update attribute: Invalid position or editor state.");
          return;
        }

        editor.commands.command(({ tr }) => {
          tr.setNodeAttribute(currentPos, "active", tabId);
          return true;
        });
      }) as EventListener;

      dom.addEventListener("tab-change", onTabChange);

      return {
        dom,
        contentDOM: dom,
        update: updatedNode => {
          const oldActive = node.attrs.active;
          const newActive = updatedNode.attrs.active;

          if (updatedNode.type !== node.type) {
            return false;
          }

          const activeAttrChanged = oldActive !== newActive;
          let otherAttrsChanged = false;

          for (const key in updatedNode.attrs) {
            if (key !== "active" && node.attrs[key] !== updatedNode.attrs[key]) {
              otherAttrsChanged = true;
              break;
            }
          }

          if (!otherAttrsChanged) {
            for (const key in node.attrs) {
              if (key !== "active" && !(key in updatedNode.attrs)) {
                otherAttrsChanged = true;
                break;
              }
            }
          }

          node = updatedNode;

          if (otherAttrsChanged) {
            return false;
          }

          if (activeAttrChanged) {
            updateChildVisibility(newActive);
          }

          setNodeViewAttrs(dom, updatedNode.attrs);

          return true;
        },
        destroy: () => {
          if (activeAnimationTimeoutId !== null) {
            cancelAnimationFrame(activeAnimationTimeoutId);
            activeAnimationTimeoutId = null;
          }
          dom.removeEventListener("tab-change", onTabChange);
        },
      };
    };
  },

  addCommands() {
    return {
      setNWTabs:
        (options: TabsProps) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
            content: [],
          });
        },
      setActiveTab:
        (tabsId: string, tabId: string) =>
        ({ state, tr, dispatch }) => {
          const tabNode = findNodeWithPosition(state, this.name, tabsId);
          if (tabNode) {
            tr.setNodeAttribute(tabNode.pos, "active", tabId);
            dispatch?.(tr);
            return true;
          }
          return false;
        },
    };
  },
});

/**
 * Adapter for individual tab component
 */
export const NWTabAdapter = Node.create<TabProps>({
  name: "nw-tab",
  group: "block tabs",
  content: "block*",
  selectable: false,
  draggable: false,

  addAttributes() {
    return {
      id: {
        default: "",
        parseHTML: (element): string | null => element.getAttribute("id"),
      },
      heading: {
        default: "",
        parseHTML: element => element.getAttribute("heading"),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "nw-tab",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["nw-tab", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setNWTab:
        options =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
            content: [],
          });
        },
    };
  },
});
